import { useState, useCallback, useEffect, useMemo } from 'react';

import { getDefaultVendorId } from '../../../api/environment';

import { useIzteachSessionStore } from '../../../stores/izteach/session';
import { useQltAuthStore } from '../../../stores/qlt/auth';
import { buildTeacherInfo } from '../embed/teacherInfo.ts';
import { getChatIdStorageKey } from '../utils/storage.ts';

/**
 * Hook tổng hợp thông tin người dùng xác thực và quản lý ID cuộc trò chuyện nháp
 * phục vụ việc nhúng iframe AI Quiz Supporter:
 * - Trích xuất thông tin giáo viên từ QLT Auth và IZTeach Session store
 * - Phân vùng lưu trữ draftChatId trong localStorage theo từng tài khoản
 */
export function useQuizEmbedSession() {
  const [draftChatId, setDraftChatId] = useState<string | undefined>();

  const qltUser = useQltAuthStore((s) => s.user);
  const qltTeacherName = useQltAuthStore((s) => s.teacherName);
  const izSession = useIzteachSessionStore();

  const userTeacherName = typeof qltUser?.teacher === 'object' ? qltUser?.teacher?.name : undefined;
  const { kind: izKind, profileId: izProfileId, userId: izUserId, vendorId: izVendorId, email: izEmail } = izSession;
  const qltUserId = qltUser?.id;
  const qltEmail = qltUser?.email;
  const embedTeacherName = qltTeacherName || userTeacherName;
  const teacherInfo = useMemo(
    () => buildTeacherInfo({
      iz: { kind: izKind, profileId: izProfileId, userId: izUserId, vendorId: izVendorId, email: izEmail },
      qlt: { userId: qltUserId, teacherName: embedTeacherName, email: qltEmail },
      defaultVendorId: getDefaultVendorId(),
    }),
    [izKind, izProfileId, izUserId, izVendorId, izEmail, qltUserId, embedTeacherName, qltEmail]
  );

  // Đồng bộ chat ID đã lưu khi tài khoản đăng nhập thay đổi
  useEffect(() => {
    const storageKey = getChatIdStorageKey(qltUser?.id ? String(qltUser.id) : undefined);
    const saved = localStorage.getItem(storageKey);
    setDraftChatId(saved || undefined);
  }, [qltUser?.id]);

  // Cập nhật chat ID mới vào state và lưu trữ cục bộ cho tài khoản đã đăng nhập
  const handleChatIdChange = useCallback(
    (chatId: string) => {
      setDraftChatId(chatId);
      if (qltUser?.id) {
        const storageKey = getChatIdStorageKey(String(qltUser.id));
        localStorage.setItem(storageKey, chatId);
      }
    },
    [qltUser?.id]
  );

  return {
    teacherInfo,
    draftChatId,
    handleChatIdChange,
  };
}
